import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Cookie, Database, UserCheck, Share2, Clock, Mail, Lock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const LAST_UPDATED = 'June 2026';

const Section = ({ icon: Icon, title, children, testid }) => (
  <section className="mb-8" data-testid={testid}>
    <h2 className="flex items-center gap-2 text-xl md:text-2xl font-black mb-3">
      <Icon size={22} className="text-[#22c55e]" />
      {title}
    </h2>
    <div className="text-sm md:text-[15px] text-gray-700 leading-relaxed space-y-3">{children}</div>
  </section>
);

const PrivacyPolicy = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <main className="max-w-4xl mx-auto px-6 py-14" data-testid="privacy-policy-page">
      <section className="text-center mb-10">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight">Privacy &amp; Cookie Policy</h1>
        <div className="h-1 w-16 bg-[#22c55e] mx-auto mt-3" />
        <p className="text-gray-600 mt-4">How Feron Store collects, uses and protects your data.</p>
        <p className="text-xs text-gray-400 mt-2">Last updated: {LAST_UPDATED}</p>
      </section>

      <section className="bg-black text-white rounded-2xl p-6 md:p-8 mb-10 flex items-start gap-4">
        <Shield className="text-[#22c55e] shrink-0 mt-1" size={28} />
        <div>
          <h2 className="text-lg md:text-xl font-black">Your privacy matters to us</h2>
          <p className="text-white/80 text-sm md:text-base mt-2 leading-relaxed">
            This policy explains what information we collect when you shop with Feron Store, why we
            collect it, and the choices you have. We only ever use your data to run the store, fulfil
            your orders and improve your experience — we never sell it.
          </p>
        </div>
      </section>

      <Section icon={Database} title="1. Information we collect" testid="privacy-collect">
        <p>We collect the following so we can process your orders and run the store:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><b>Account details</b> — name, email address and (if you sign in with Google) your Google profile basics.</li>
          <li><b>Order &amp; delivery details</b> — shipping address, phone number and the items you buy.</li>
          <li><b>Payment confirmation</b> — handled securely by our payment provider; we never store your full card number.</li>
          <li><b>Support messages</b> — anything you send us through live chat or email.</li>
          <li><b>Technical data</b> — approximate region (to show the right currency), device and browser type.</li>
        </ul>
      </Section>

      <Section icon={Cookie} title="2. How we use cookies" testid="privacy-cookies">
        <p>Cookies are small files stored on your device. We use them in two ways:</p>
        <div className="grid md:grid-cols-2 gap-4 mt-2">
          <div className="border-2 border-[#22c55e]/40 bg-[#22c55e]/5 rounded-xl p-5">
            <h3 className="font-black text-base mb-2">Essential cookies</h3>
            <p className="text-sm text-gray-700">Keep your cart and wishlist, remember your region/currency and keep you signed in. The store can't work without these, so they're always on.</p>
          </div>
          <div className="border-2 border-gray-200 bg-neutral-50 rounded-xl p-5">
            <h3 className="font-black text-base mb-2">Preference cookies</h3>
            <p className="text-sm text-gray-700">Remember choices like your language and your cookie-consent decision so we don't ask again on every visit.</p>
          </div>
        </div>
        <p className="mt-3">
          When you first visit, our cookie banner lets you <b>Accept all</b> or continue with
          <b> Essentials only</b>. Your choice is stored locally in your browser and you can clear it
          anytime from your browser settings to see the banner again.
        </p>
      </Section>

      <Section icon={UserCheck} title="3. How we use your data" testid="privacy-use">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>To process, fulfil and deliver your orders.</li>
          <li>To send order confirmations, tracking updates and support replies.</li>
          <li>To show prices in your local currency and keep the store secure.</li>
          <li>To respond to your questions through live chat and email.</li>
        </ul>
        <p>We do <b>not</b> sell or rent your personal data to anyone.</p>
      </Section>

      <Section icon={Share2} title="4. Who we share it with" testid="privacy-share">
        <p>We only share the minimum needed with trusted providers who help us run the store:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><b>Payment provider</b> — to process your payment securely.</li>
          <li><b>Shipping &amp; courier partners</b> — to deliver your order.</li>
          <li><b>Email provider</b> — to send transactional emails such as confirmations and tracking.</li>
        </ul>
        <p>Each partner is only allowed to use your data to perform their service for us.</p>
      </Section>

      <Section icon={Lock} title="5. Your rights (GDPR)" testid="privacy-rights">
        <p>If you're in the EU/EEA, you have the right to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Access the personal data we hold about you.</li>
          <li>Ask us to correct or delete your data.</li>
          <li>Withdraw consent and object to certain processing.</li>
          <li>Request a copy of your data (portability).</li>
        </ul>
        <p>To exercise any of these, just email us and we'll respond within 30 days.</p>
      </Section>

      <Section icon={Clock} title="6. How long we keep it" testid="privacy-retention">
        <p>
          We keep order records for as long as needed to fulfil the order and meet our legal and tax
          obligations. Account data is kept until you ask us to delete your account.
        </p>
      </Section>

      <div className="text-center bg-black text-white rounded-2xl p-8 mt-10">
        <Mail className="text-[#22c55e] mx-auto mb-3" size={28} />
        <h3 className="font-black text-xl mb-2">Questions about your privacy?</h3>
        <p className="text-white/70 mb-4 text-sm">
          Email us at <a href="mailto:feronstore@web.de" className="text-[#22c55e] underline">feronstore@web.de</a> and we'll help.
        </p>
        <Link to="/contact-us" className="inline-block bg-[#22c55e] hover:bg-[#16a34a] text-black font-black px-6 py-3 rounded-full tracking-wider transition" data-testid="privacy-contact-cta">
          CONTACT US
        </Link>
      </div>
    </main>
    <Footer />
  </div>
);

export default PrivacyPolicy;
